import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import {
  Plus, Search, Edit3, Trash2, ChevronDown, Package, Minus
} from 'lucide-react';
import { formatPrice } from '../../utils/price';
import { useApp } from '../../context/AppContext';
import { toast, confirmDialog } from '../../components/Toast/toast';

// 1 товар, 2–4 товара, 5+ товаров (11–14 — товаров)
function pluralizeProducts(n) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'товар';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'товара';
  return 'товаров';
}

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const navigate = useNavigate();
  const { user } = useApp();
  const isManager = user?.is_admin && user?.role === 'manager';
  const [updatingQty, setUpdatingQty] = useState({});
  // Быстрое редактирование владельца: раскрытая строка в списке товаров
  const [expandedId, setExpandedId] = useState(null);

  // Цена безразмерного товара из списка (поле в раскрытой строке)
  const saveProductPrice = async (productId, raw) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const value = Number(raw);
    if (!(value > 0)) {
      toast.error('Цена должна быть больше нуля');
      return;
    }
    if (Number(product.price) === value) return;
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, price: value } : p));
    setUpdatingQty(prev => ({ ...prev, [productId]: true }));
    try {
      await client.put(`/admin/products/${productId}`, {
        name: product.name,
        category_id: product.category_id,
        description: product.description || '',
        care: product.care || '',
        price: value,
        discount_percent: product.discount_percent ?? null,
        image_url: product.image_url || '',
        quantity: product.quantity ?? 0,
        characteristics: product.characteristics || {},
        // variants не шлём — не трогаем (изменение цены только общей)
      });
      toast.success('Цена сохранена');
    } catch {
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, price: product.price } : p));
      toast.error('Не удалось сохранить цену');
    } finally {
      setUpdatingQty(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Цена размера из списка — отдельный эндпоинт, id размеров не меняются
  const saveVariantPrice = async (productId, variantId, raw) => {
    const product = products.find(p => p.id === productId);
    const variant = product?.variants?.find(v => v.id === variantId);
    if (!variant) return;
    const value = Number(raw);
    if (!(value > 0)) {
      toast.error('Цена должна быть больше нуля');
      return;
    }
    if (Number(variant.price) === value) return;
    setUpdatingQty(prev => ({ ...prev, [productId]: true }));
    try {
      const res = await client.put(
        `/admin/products/${productId}/variants/${variantId}/price`,
        { price: value }
      );
      setProducts(prev => prev.map(p => p.id === productId ? res.data : p));
      toast.success('Цена сохранена');
    } catch {
      toast.error('Не удалось сохранить цену');
    } finally {
      setUpdatingQty(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Содержимое быстрого поля: цена + остатки (как у менеджера)
  // Степпер остатка: общий для панели (безразмерный или конкретный размер)
  const renderStepper = (p, variant) => {
    const qty = variant ? (variant.quantity ?? 0) : (p.quantity ?? 0);
    const onDec = () => (variant ? changeVariantQuantity(p.id, variant.id, -1) : changeQuantity(p.id, -1));
    const onInc = () => (variant ? changeVariantQuantity(p.id, variant.id, 1) : changeQuantity(p.id, 1));
    return (
      <div className="flex shrink-0 items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onDec}
          disabled={updatingQty[p.id] || qty <= 0}
          className="grid h-7 w-7 place-items-center rounded-lg bg-[#e2e8f0] text-[#64748b] transition hover:bg-[#d5dce4] active:scale-90 disabled:opacity-30"
          aria-label="Меньше"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className={`w-7 text-center text-sm font-bold ${qty <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
          {qty}
        </span>
        <button
          type="button"
          onClick={onInc}
          disabled={updatingQty[p.id]}
          className="grid h-7 w-7 place-items-center rounded-lg bg-[#6B9B74]/15 text-[#6B9B74] transition hover:bg-[#6B9B74]/25 active:scale-90 disabled:opacity-30"
          aria-label="Больше"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  // Десктоп: строки быстрого редактирования, выровненные по колонкам таблицы
  // (подпись → «Размеры», цена → «Цена», степпер → «Остаток»)
  const renderQuickRows = (p) => {
    const isSizeless = !(p.variants || []).length;
    const items = isSizeless
      ? [{ id: null, label: null, price: p.price }]
      : (p.variants || []).map(v => ({ id: v.id, label: v.label, price: v.price, quantity: v.quantity }));

    return items.map((item, idx) => (
      <tr key={item.id || 'q'} className="border-b border-[#e2e8f0] bg-[#f8fafc]">
        <td colSpan={2} className="px-4 py-1.5">
          {idx === 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">
              Цена и остаток
            </span>
          )}
        </td>
        <td className="px-4 py-1.5 text-xs font-semibold text-[#374151]">
          {item.label || '—'}
        </td>
        <td className="px-4 py-1.5 text-right">
          <input
            key={`qp-${item.id || 'main'}-${item.price}`}
            type="number"
            min="0"
            defaultValue={item.price}
            disabled={updatingQty[p.id]}
            onBlur={e => (
              isSizeless
                ? saveProductPrice(p.id, e.target.value)
                : saveVariantPrice(p.id, item.id, e.target.value)
            )}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            aria-label="Цена"
            className="w-24 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1 text-right text-sm font-bold text-[#0f172a] outline-none transition focus:border-[#6B9B74] disabled:opacity-50"
          />
        </td>
        <td className="px-4 py-1.5 text-right">
          {renderStepper(p, isSizeless ? null : items[idx])}
        </td>
        <td className="px-4 py-1.5" />
      </tr>
    ));
  };

  const renderQuickPanel = (p) => {
    const isSizeless = !(p.variants || []).length;
    if (isSizeless) {
      return (
        <div className="flex flex-wrap items-center justify-end gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b]">Цена, ₽</span>
            <input
              key={`price-${p.id}-${p.price}`}
              type="number"
              min="0"
              defaultValue={p.price}
              disabled={updatingQty[p.id]}
              onBlur={e => saveProductPrice(p.id, e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              className="w-28 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm font-bold text-[#0f172a] outline-none transition focus:border-[#6B9B74] disabled:opacity-50"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b]">Остаток</span>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => changeQuantity(p.id, -1)}
                disabled={updatingQty[p.id] || (p.quantity ?? 0) <= 0}
                className="grid h-7 w-7 place-items-center rounded-lg bg-[#e2e8f0] text-[#64748b] transition hover:bg-[#d5dce4] active:scale-90 disabled:opacity-30"
                aria-label="Меньше"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className={`w-7 text-center text-sm font-bold ${(p.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                {p.quantity ?? 0}
              </span>
              <button
                type="button"
                onClick={() => changeQuantity(p.id, 1)}
                disabled={updatingQty[p.id]}
                className="grid h-7 w-7 place-items-center rounded-lg bg-[#6B9B74]/15 text-[#6B9B74] transition hover:bg-[#6B9B74]/25 active:scale-90 disabled:opacity-30"
                aria-label="Больше"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-1.5">
        {p.variants.map(v => (
          <div key={v.id} className="flex flex-wrap items-center justify-end gap-4">
            <span className="min-w-0 truncate text-xs font-semibold text-[#374151]">{v.label}</span>
            <div className="flex items-center gap-2">
              <input
                key={`vprice-${v.id}-${v.price}`}
                type="number"
                min="0"
                defaultValue={v.price}
                disabled={updatingQty[p.id]}
                onBlur={e => saveVariantPrice(p.id, v.id, e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="w-24 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1 text-sm font-bold text-[#0f172a] outline-none transition focus:border-[#6B9B74] disabled:opacity-50"
                aria-label={`Цена ${v.label}`}
              />
              <button
                type="button"
                onClick={() => changeVariantQuantity(p.id, v.id, -1)}
                disabled={updatingQty[p.id] || (v.quantity ?? 0) <= 0}
                className="grid h-7 w-7 place-items-center rounded-lg bg-[#e2e8f0] text-[#64748b] transition hover:bg-[#d5dce4] active:scale-90 disabled:opacity-30"
                aria-label="Меньше"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className={`w-7 text-center text-sm font-bold ${(v.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                {v.quantity ?? 0}
              </span>
              <button
                type="button"
                onClick={() => changeVariantQuantity(p.id, v.id, 1)}
                disabled={updatingQty[p.id]}
                className="grid h-7 w-7 place-items-center rounded-lg bg-[#6B9B74]/15 text-[#6B9B74] transition hover:bg-[#6B9B74]/25 active:scale-90 disabled:opacity-30"
                aria-label="Больше"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Товар без размеров: прежний − N + через PUT товара
  const changeQuantity = async (productId, delta) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const newQty = Math.max(0, (product.quantity ?? 0) + delta);
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, quantity: newQty } : p));
    setUpdatingQty(prev => ({ ...prev, [productId]: true }));
    try {
      await client.put(`/admin/products/${productId}`, {
        name: product.name,
        category_id: product.category_id,
        description: product.description || '',
        care: product.care || '',
        price: product.price ?? 0,
        discount_percent: product.discount_percent ?? null,
        image_url: product.image_url || '',
        quantity: newQty,
        characteristics: product.characteristics || {},
      });
    } catch {
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, quantity: product.quantity } : p));
    } finally {
      setUpdatingQty(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Товар с размерами: − N + у КАЖДОГО размера через отдельный эндпоинт
  const changeVariantQuantity = async (productId, variantId, delta) => {
    const product = products.find(p => p.id === productId);
    const variant = product?.variants?.find(v => v.id === variantId);
    if (!variant) return;
    const newQty = Math.max(0, (variant.quantity ?? 0) + delta);
    // сериализация в пределах товара — один запрос в полёте, иначе
    // out-of-order ответы показывают устаревший остаток (ревью Task 6)
    setProducts(prev => prev.map(p =>
      p.id === productId
        ? { ...p, variants: p.variants.map(v => v.id === variantId ? { ...v, quantity: newQty } : v) }
        : p
    ));
    setUpdatingQty(prev => ({ ...prev, [productId]: true }));
    try {
      const res = await client.put(
        `/admin/products/${productId}/variants/${variantId}/quantity`,
        { quantity: newQty }
      );
      // Сервер вернул товар с пересчитанными quantity/in_stock
      setProducts(prev => prev.map(p => (p.id === productId ? res.data : p)));
    } catch {
      setProducts(prev => prev.map(p =>
        p.id === productId
          ? { ...p, variants: p.variants.map(v => v.id === variantId ? { ...v, quantity: variant.quantity } : v) }
          : p
      ));
    } finally {
      setUpdatingQty(prev => ({ ...prev, [productId]: false }));
    }
  };

  const loadProducts = useCallback((signal) => {
    setLoading(true);
    setLoadError('');
    const params = { page_size: 100 }; // админка: все товары, не 20 со страницы
    if (search) params.search = search;
    if (categoryFilter) params.category = categoryFilter;
    client.get('/catalog/products', { params, signal })
      .then(res => {
        setProducts(res.data.items || []);
        setTotal(res.data.total ?? (res.data.items || []).length);
      })
      .catch(err => {
        if (err.name === 'CanceledError') return;
        setProducts([]);
        setLoadError('Не удалось загрузить товары');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, [search, categoryFilter]);

  useEffect(() => {
    const controller = new AbortController();
    // набор текста → 300 мс тишины; смена фильтра категории → сразу
    const timer = setTimeout(() => loadProducts(controller.signal), search ? 300 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort(); // отменяем предыдущий запрос — гонка ответов исключена
    };
  }, [loadProducts]);

  useEffect(() => {
    client.get('/catalog/categories')
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog('Удалить товар?', {
      danger: true,
      okLabel: 'Удалить',
      cancelLabel: 'Отмена',
      detail: 'Товар будет удалён навсегда. Связанные данные (избранное, уведомления) также будут удалены.',
      onConfirm: async () => {
        try {
          await client.delete(`/admin/products/${id}`);
          loadProducts();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Не удалось удалить товар');
          throw err;
        }
      },
    });
    if (!confirmed) return;
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id || String(c.id) === String(id));
    return cat ? cat.name : '—';
  };

  // сортировка десктоп-таблицы (на мобиле не применяется)
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const sortByHeader = (key) => {
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortProducts = (list) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.key === 'price') return (Number(a.price || 0) - Number(b.price || 0)) * dir;
      if (sort.key === 'qty') return (Number(a.quantity || 0) - Number(b.quantity || 0)) * dir;
      if (sort.key === 'cat') return getCategoryName(a.category_id).localeCompare(getCategoryName(b.category_id)) * dir;
      return String(a.name || '').localeCompare(String(b.name || ''), 'ru') * dir;
    });
  };

  const th = (key, label, align = 'text-left') => (
    <th className={`px-4 py-3 ${align}`}>
      <button
        type="button"
        onClick={() => sortByHeader(key)}
        className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition hover:opacity-70 ${
          sort.key === key ? 'text-[#0f172a]' : 'text-[#64748b]'
        }`}
      >
        {label}
        <span className={sort.key === key ? 'text-[#374151]' : 'text-[#cbd5e1]'}>
          {sort.key === key && sort.dir === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );

  const renderProductsTable = (list) => (
    <div className="hidden overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white shadow-sm md:block">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
            {th('name', 'Товар')}
            {th('cat', 'Категория')}
            {!isManager && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#64748b]">Размеры</th>}
            {!isManager && th('price', 'Цена', 'text-right')}
            {th('qty', 'Остаток', 'text-right')}
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[#64748b]">Действия</th>
          </tr>
        </thead>
        <tbody>
          {sortProducts(list).map(p => (
            <React.Fragment key={p.id}>
            <tr
              onClick={isManager ? undefined : () => setExpandedId(expandedId === p.id ? null : p.id)}
              className={`border-b border-[#e2e8f0] transition last:border-0 hover:bg-[#f8fafc]${
                isManager ? '' : ' cursor-pointer'
              }`}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-[#f1f5f9]">
                    {p.image_url
                      ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                      : <div className="flex h-full w-full items-center justify-center text-base">🌱</div>}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-[#0f172a]">{p.name}</div>
                    {Number(p.discount_percent) > 0 && (
                      <span className="rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                        −{p.discount_percent}%
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-[#64748b]">{getCategoryName(p.category_id)}</td>
              {!isManager && (
                <td className="px-4 py-3 text-[#64748b]">{(p.variants || []).length || '—'}</td>
              )}
              {!isManager && (
                <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-[#0f172a]">
                  {p.quantity > 0
                    ? ((p.variants || []).length > 0 ? `от ${formatPrice(p.price)} ₽` : `${formatPrice(p.price)} ₽`)
                    : <span className="text-[#D64545]">Нет в наличии</span>}
                </td>
              )}
              <td className={`whitespace-nowrap px-4 py-3 text-right font-bold ${
                (p.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'
              }`}>
                {p.quantity ?? 0}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1.5">
                  {isManager && (p.variants || []).length === 0 && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => changeQuantity(p.id, -1)}
                        disabled={updatingQty[p.id] || (p.quantity ?? 0) <= 0}
                        className="grid h-7 w-7 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                        aria-label="Меньше"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => changeQuantity(p.id, 1)}
                        disabled={updatingQty[p.id]}
                        className="grid h-7 w-7 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                        aria-label="Больше"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {!isManager && (
                    <>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/admin/products/${p.id}`);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a] active:scale-90"
                        aria-label="Редактировать"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          handleDelete(p.id);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#D64545]/10 hover:text-[#D64545] active:scale-90"
                        aria-label="Удалить"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
            {/* Владелец: клик по строке — быстрое поле под ней, в столбиках таблицы */}
            {!isManager && expandedId === p.id && renderQuickRows(p)}
            {/* Менеджер: остатки по размерам — та же кнопка ±, что на мобиле.
                Строка прижата ВПРАВО: кнопки размеров стоят под колонкой
                «Остаток/Действия», чтобы не бегать по экрану при смене количества */}
            {isManager && (p.variants || []).length > 0 && (
              <tr className="bg-[#f8fafc]">
                <td colSpan={4} className="px-4 pb-3 pt-0">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-[#64748b]">Размеры:</span>
                    {p.variants.map(v => (
                      <div key={v.id} className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-2 py-1">
                        <span className="text-xs font-medium text-[#64748b]">{v.label}</span>
                        <button
                          type="button"
                          onClick={() => changeVariantQuantity(p.id, v.id, -1)}
                          disabled={updatingQty[p.id] || (v.quantity ?? 0) <= 0}
                          className="grid h-6 w-6 place-items-center rounded-md bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                          aria-label="Меньше"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className={`w-6 text-center text-xs font-bold ${(v.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                          {v.quantity ?? 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => changeVariantQuantity(p.id, v.id, 1)}
                          disabled={updatingQty[p.id]}
                          className="grid h-6 w-6 place-items-center rounded-md bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                          aria-label="Больше"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Счётчик товаров */}
      <h2 className="text-sm font-medium text-[#64748b]">
        {loading && total === 0 ? 'Загрузка…' : `${total} ${pluralizeProducts(total)}`}
      </h2>

      {/* Поиск */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
        <input
          type="text"
          placeholder="Поиск товаров..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[#374151] focus:bg-white"
        />
      </div>

      {/* Фильтр и кнопка */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="w-full appearance-none rounded-xl border border-[#e2e8f0] bg-[#f8fafc] py-2.5 pl-3 pr-8 text-sm outline-none transition focus:border-[#6B9B74] focus:bg-white"
          >
            <option value="">Все категории</option>
            {categories.map(c => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
        </div>
        {!isManager && (
          <button
            onClick={() => navigate('/admin/products/new')}
            className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#374151] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.97]"
          >
            <Plus className="h-4 w-4" />
            Добавить
          </button>
        )}
      </div>

      {/* Список товаров */}
      {loadError && (
        <div className="rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {loadError}
        </div>
      )}
      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          Загрузка...
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <Package className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>Товары не найдены</p>
        </div>
      ) : (
        <>
        {renderProductsTable(products)}
        <div className="space-y-2 md:hidden">
          {products.map(p => (
            <div
              key={p.id}
              onClick={isManager ? undefined : () => setExpandedId(expandedId === p.id ? null : p.id)}
              className={`rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm ${
                isManager ? '' : 'cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-3">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#f1f5f9]">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-2xl">🌱</div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-[#0f172a]">{p.name}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className="truncate text-xs text-[#64748b]">{getCategoryName(p.category_id)}</span>
                  {Number(p.discount_percent) > 0 && (
                    <span className="shrink-0 rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                      −{p.discount_percent}%
                    </span>
                  )}
                </div>
                {!isManager && (
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className={`text-sm font-bold ${p.quantity <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                      {p.quantity > 0
                        ? ((p.variants || []).length > 0 ? `от ${formatPrice(p.price)} ₽` : `${formatPrice(p.price)} ₽`)
                        : 'Нет в наличии'}
                    </span>
                    {p.quantity > 0 && (
                      <span className="text-xs text-[#64748b]">({p.quantity} шт)</span>
                    )}
                  </div>
                )}
              </div>

                {!isManager && (
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/admin/products/${p.id}`);
                      }}
                      className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a] active:scale-90"
                      aria-label="Редактировать"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(p.id);
                      }}
                      className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#D64545]/10 hover:text-[#D64545] active:scale-90"
                      aria-label="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {/* Менеджер без размеров: тот же размер кнопок, что у вариантов, и справа в ряду */}
              {isManager && (p.variants || []).length === 0 && (
                <div className="mt-2 flex items-center justify-end border-t border-[#f1f5f9] pt-2">
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => changeQuantity(p.id, -1)}
                      disabled={updatingQty[p.id] || (p.quantity ?? 0) <= 0}
                      className="grid h-7 w-7 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className={`w-7 text-center text-sm font-bold ${(p.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                      {p.quantity ?? 0}
                    </span>
                    <button
                      onClick={() => changeQuantity(p.id, 1)}
                      disabled={updatingQty[p.id]}
                      className="grid h-7 w-7 place-items-center rounded-lg bg-[#6B9B74]/15 text-[#6B9B74] transition hover:bg-[#6B9B74]/25 active:scale-90 disabled:opacity-30"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
              {/* Менеджер: размеры отдельным блоком ПОД названием — название не обрезается */}
              {isManager && (p.variants || []).length > 0 && (
                <div className="mt-2 space-y-1.5 border-t border-[#f1f5f9] pt-2">
                  {p.variants.map(v => (
                    <div key={v.id} className="flex items-center justify-between gap-1.5">
                      <span className="min-w-0 truncate text-xs font-medium text-[#64748b]">{v.label}</span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          onClick={() => changeVariantQuantity(p.id, v.id, -1)}
                          disabled={updatingQty[p.id] || (v.quantity ?? 0) <= 0}
                          className="grid h-7 w-7 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition hover:bg-[#e2e8f0] active:scale-90 disabled:opacity-30"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className={`w-7 text-center text-sm font-bold ${(v.quantity ?? 0) <= 0 ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
                          {v.quantity ?? 0}
                        </span>
                        <button
                          onClick={() => changeVariantQuantity(p.id, v.id, 1)}
                          disabled={updatingQty[p.id]}
                          className="grid h-7 w-7 place-items-center rounded-lg bg-[#6B9B74]/15 text-[#6B9B74] transition hover:bg-[#6B9B74]/25 active:scale-90 disabled:opacity-30"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Владелец: клик по карточке — быстрое поле (цена + остатки) */}
              {!isManager && expandedId === p.id && (
                <div
                  className="mt-2 border-t border-[#f1f5f9] pt-2"
                  onClick={e => e.stopPropagation()}
                >
                  {renderQuickPanel(p)}
                </div>
              )}
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
